import useAppStore from '$appStore'
import dayjs, { Dayjs } from 'dayjs'
import { CompanyPreferences, PermissionType } from './appContext'
import { Country, Locale, PayrollSystem } from '$constants'
import { ActiveCompany, CompanySettings, Nullable, CoreApiError } from '$models'
import { LoadedError, logger, singletonQueryClient } from '$utils'
import { enqueueLoadedSnackbar } from './snackbar'
import { NumberParser } from 'src/components/common/helpers/numberParser'
import { CancelOptions, QueryFilters } from '@tanstack/react-query'

/**
 * Returns true if the mobile breakpoint is currently active.
 */
export const isMobile = () => window.matchMedia('(max-width: 991px)').matches

/** Checks if a payroll system value is a Xero variant */
export const isXeroPayrollSystem = (
  value: PayrollSystem | null | undefined
): boolean => value === PayrollSystem.XeroAu || value === PayrollSystem.XeroNz

/**
 * Get query string params from string url
 * @param urlToParse
 * @returns { [key: string]: string }
 */
export const getQueryStringParamsFromStringUrl = (urlToParse: string) => {
  const url = new URL(urlToParse)
  const searchParams = url.searchParams
  const queryParams: { [key: string]: string } = {}
  for (const [key, value] of searchParams.entries()) {
    queryParams[key] = value
  }

  return queryParams
}

/**
 * Function to avoid sending empty params to API
 * @param data
 * @returns data without empty fields
 */
export const removeEmptyFields = (data) => {
  Object.keys(data).forEach((key) => {
    if (data[key] == null || data[key].length === 0) {
      delete data[key]
    }
  })

  return data
}

/**
 * Create query string parameters from object.
 *
 * Note that keys and values are uri encoded before being returned.
 * @param parameters object with key value
 * @returns
 */
export const createQueryString = (parameters: object) => {
  const queryPairs: string[] = []

  Object.entries(parameters)
    .filter(
      ([_, value]) => value !== null && value !== undefined && value !== ''
    )
    .forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Create separate query parameters for each array value
        value.forEach((item) => {
          if (item !== null && item !== undefined && item !== '') {
            queryPairs.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(item)}`
            )
          }
        })
      } else {
        queryPairs.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
      }
    })

  return queryPairs.join('&')
}

export const splitAndLowerCaseAndInsertSpaces = (
  inputString: string
): string => {
  const wordsArray = inputString.split(/(?=[A-Z])/)
  const modifiedString = wordsArray.map((word) => word.toLowerCase()).join(' ')
  return modifiedString
}

/**
 * @deprecated START USING transformStringToNumberProperty
 * INSTEAD AS IT TAKES LOCALE INTO ACCOUNT FOR NUMBER PARSING
 * Transform a currency string into number removing the commas
 * NOTE: This return a number if there is a number in the string
 *       e.g the string '50 L' goes in then it will return the number 50
 * @param property string to convert to number removing the commas
 * @returns
 */
export const transformNumberProperty = (property: string): number => {
  if (property === null || property === undefined) {
    return 0
  }
  const stringWithoutComma = property.replace(/,/g, '')

  return parseFloat(stringWithoutComma)
}

export const transformStringToNumberProperty = (
  property: string | null | undefined
): number => {
  try {
    if (property === null || property === undefined) {
      return 0
    }

    const valueAsString = String(property).trim()

    const companyLocale: string = useAppStore
      .getState()
      .activeCompany?.preference(CompanyPreferences.Locale) as string

    const LocaleAwareParser = new NumberParser(companyLocale)
    const parsed = LocaleAwareParser.parse(valueAsString)

    if (
      Number.isNaN(parsed) ||
      !Number.isFinite(parsed) ||
      Math.abs(parsed) > Number.MAX_SAFE_INTEGER
    ) {
      return 0
    }

    return parsed
  } catch {
    return 0
  }
}

/**
 * Set Company DayStart Preference to a date
 * @param date with updated DayStart preference
 * @returns
 */
export const setCompanyDayStartToDate = (date: Dayjs) => {
  const dayStartPreference = useAppStore
    .getState()
    .activeCompany?.preference(CompanyPreferences.DayStart)
  const activeCompanyDayStartPreference: string[] =
    dayStartPreference?.split(':') || '05:00:00'

  date = date
    .set('hour', +activeCompanyDayStartPreference[0])
    .set('minute', +activeCompanyDayStartPreference[1])
    .set('second', +activeCompanyDayStartPreference[2])

  return date
}

/**
 * Date sort comparator for lists
 * @param value1 date string to compare
 * @param value2 date string to compare
 */
export const dateSortComparator = (value1: string, value2: string) => {
  const valueToSort1 = dayjs(value1)
  const valueToSort2 = dayjs(value2)
  if (valueToSort1.isBefore(valueToSort2)) {
    return -1
  } else if (valueToSort1.isAfter(valueToSort2)) {
    return 1
  } else {
    return 0
  }
}

/**
 * Trigger the in-app support dialog to open over the current page.
 */
export const raiseSupportDialog = () => {
  // Note today the support dialg is implemented in the AngularJS app,
  // so we simply emit an event here, and the AngularJS app listens for it
  // to do the actual opening of the dialog.
  window.dispatchEvent(new CustomEvent('@@loaded/open-support-dialog'))
}

/**
 * Dynamic input formatter while the user is typing
 * @param value to format
 * @param maxLength max length of the input
 * @param separatorPositions positions where the separator has to be inserted
 * @param separator to use
 * @param regExp to use, by default -> non-numeric characters except hyphen and backspace
 * @returns formatted value
 */
export const inputFormatter = (
  value: string,
  maxLength?: number,
  separatorPositions?: number[],
  separator?: string,
  regExp: RegExp = /[^0-9\\-]/g
) => {
  let formattedInput = value
    // Replace with the provided RegExp
    .replace(regExp, '')
  // Limit length to maxLength

  if (maxLength) {
    formattedInput = formattedInput.slice(0, maxLength)
  }

  if (separator && separatorPositions) {
    for (let i: number = 0; i <= separatorPositions.length; i++) {
      if (
        formattedInput.length === separatorPositions[i] &&
        !formattedInput.endsWith(separator)
      ) {
        formattedInput += separator
      }
    }
  }

  return formattedInput
}

/**
 * Given a string value it returns a new string with the separator inserted in the defined positions
 * @param value to format
 * @param separatorPositions positions where the separator has to be inserted
 * @param separator to use
 * @returns formatted value
 */
export const valueFormatter = (
  value: string,
  separatorPositions: number[],
  separator: string
) => {
  if (!value) return ''
  // Remove all the existing separators to have a clean string
  const newValue = value.replaceAll(separator, '')
  // Initialize an array to store the substrings
  const substrings: string[] = []
  // Start index to keep track of the current position in the input string
  let startIndex = 0

  // Iterate through the positions array
  for (const pos of separatorPositions) {
    // Extract the substring from the input string
    const substring = newValue.substring(startIndex, pos)

    // Append the substring to the substrings array
    substrings.push(substring)

    // Update the start index for the next iteration
    startIndex = pos
  }

  // Append the remaining substring (if any) after the last specified position
  const remainingSubstring = newValue.substring(
    separatorPositions[separatorPositions.length - 1]
  )
  if (remainingSubstring) {
    substrings.push(remainingSubstring)
  }

  // Join the substrings array with the separator
  return substrings.join(separator)
}

export const getActiveCompanyCountry = () => {
  const activeCompany = useAppStore.getState().activeCompany
  const localePreference: Locale =
    activeCompany?.preference(CompanyPreferences.Locale) || Locale.NZ

  return getCountryPreferenceBasedOnLocale(localePreference)
}

export const getCountryPreferenceBasedOnLocale = (localePreference: Locale) => {
  let countryPreference: Country
  switch (localePreference) {
    case Locale.AU:
      countryPreference = Country.Australia
      break
    case Locale.NZ:
    default:
      countryPreference = Country.NewZealand
      break
  }
  return countryPreference
}

export const constructActiveCompanySettings = (
  activeCompany: ActiveCompany | null
) => {
  const hasTimePermission =
    activeCompany?.hasPermission(PermissionType.Time) ?? false

  const hasSalaryPermission =
    activeCompany?.hasPermission(PermissionType.Salary) ?? false

  const hasPayrollPermission =
    activeCompany?.hasPermission(PermissionType.Payroll) ?? false

  const hasManagerPermission =
    activeCompany?.hasPermission(PermissionType.Manager) ?? false

  const hasStockPermission =
    activeCompany?.hasPermission(PermissionType.Stock) ?? false

  const weekStartPreference: number = activeCompany?.preference(
    CompanyPreferences.WeekStart
  )
    ? +activeCompany?.preference(CompanyPreferences.WeekStart)
    : 0

  const dayStartPreference: string =
    activeCompany?.preference(CompanyPreferences.DayStart) ?? '05:00:00'

  const localePreference: Locale =
    activeCompany?.preference(CompanyPreferences.Locale) ?? Locale.NZ

  const timeZonePreference: string =
    activeCompany?.preference(CompanyPreferences.TimeZone) ?? dayjs.tz?.guess()

  const provincePreference: string | undefined = activeCompany?.preference(
    CompanyPreferences.Province
  )

  const payrollSystemPreference: PayrollSystem | null =
    activeCompany?.preference(CompanyPreferences.PayrollSystem)

  const defaultFreightTaxRatePreference: string = activeCompany?.preference(
    CompanyPreferences.DefaultFreightTaxRate
  )

  const clockinRoundingPreference: string = activeCompany?.preference(
    CompanyPreferences.ClockinRounding
  )

  const breakRoundingTargetPreference: string | null =
    activeCompany?.preference(CompanyPreferences.BreakRoundingTarget) ?? null

  const breakRoundingMinimumPreference: string | null =
    activeCompany?.preference(CompanyPreferences.BreakRoundingMinimum) ?? null

  const overrideSalesTaxRatePreference: boolean =
    activeCompany?.preference(CompanyPreferences.OverrideSalesTaxRate) === '1'

  const includePosClockinsPreference: boolean =
    activeCompany?.preference(CompanyPreferences.IncludePosClockins) === '1'

  const timeAllowsPaySummaryPreference: boolean =
    activeCompany?.preference(
      CompanyPreferences.TimePermissionAllowsWageAndSalarySummary
    ) === '1'

  const countryPreference: Country =
    getCountryPreferenceBasedOnLocale(localePreference)

  const timeRosterPublicTokenPreference: string | null =
    activeCompany?.preference(CompanyPreferences.TimeRosterPublicToken)

  const xeroFreightAccountPreference: string | null = activeCompany?.preference(
    CompanyPreferences.XeroFreightAccount
  )

  const teamAppEnabled: boolean =
    activeCompany?.preference(CompanyPreferences.TeamAppEnabled) === '1'

  const teamAppDisplayPayInformation: boolean =
    activeCompany?.preference(
      CompanyPreferences.TeamAppDisplayPayInformation
    ) === '1'

  const teamAppDisplayPhoneNumbers: boolean =
    activeCompany?.preference(CompanyPreferences.TeamAppDisplayPhoneNumbers) ===
    '1'

  return {
    countryPreference,
    weekStartPreference,
    dayStartPreference,
    localePreference,
    timeZonePreference,
    provincePreference,
    payrollSystemPreference,
    hasPayrollPermission,
    hasManagerPermission,
    hasTimePermission,
    hasSalaryPermission,
    hasStockPermission,
    defaultFreightTaxRatePreference,
    overrideSalesTaxRatePreference,
    includePosClockinsPreference,
    timeAllowsPaySummaryPreference,
    clockinRoundingPreference,
    breakRoundingTargetPreference,
    breakRoundingMinimumPreference,
    timeRosterPublicTokenPreference,
    xeroFreightAccountPreference,
    teamAppEnabled,
    teamAppDisplayPayInformation,
    teamAppDisplayPhoneNumbers,
  }
}

let cachedCompanySettings: {
  companyId: string | undefined
  settings: CompanySettings
} | null = null

export const getActiveCompanySettings = (): CompanySettings => {
  const activeCompany = useAppStore.getState().activeCompany
  const companyId = activeCompany?.id

  if (cachedCompanySettings && cachedCompanySettings.companyId === companyId) {
    return cachedCompanySettings.settings
  }

  const companySettings = constructActiveCompanySettings(activeCompany)

  const settings: CompanySettings = {
    address: activeCompany?.address,
    name: activeCompany?.name,
    tier: activeCompany?.tier,
    pos: activeCompany?.pos,
    invoiceEmail: activeCompany?.invoiceEmail,
    countryPreference: companySettings.countryPreference,
    weekStartPreference: companySettings.weekStartPreference,
    dayStartPreference: companySettings.dayStartPreference,
    localePreference: companySettings.localePreference,
    timeZonePreference: companySettings.timeZonePreference,
    provincePreference: companySettings.provincePreference,
    payrollSystemPreference: companySettings.payrollSystemPreference,
    hasPayrollPermission: companySettings.hasPayrollPermission,
    hasManagerPermission: companySettings.hasManagerPermission,
    hasTimePermission: companySettings.hasTimePermission,
    hasSalaryPermission: companySettings.hasSalaryPermission,
    hasStockPermission: companySettings.hasStockPermission,
    defaultFreightTaxRatePreference:
      companySettings.defaultFreightTaxRatePreference,
    overrideSalesTaxRatePreference:
      companySettings.overrideSalesTaxRatePreference,
    includePosClockinsPreference: companySettings.includePosClockinsPreference,
    timeAllowsPaySummaryPreference:
      companySettings.timeAllowsPaySummaryPreference,
    clockinRoundingPreference: companySettings.clockinRoundingPreference,
    breakRoundingTargetPreference:
      companySettings.breakRoundingTargetPreference,
    breakRoundingMinimumPreference:
      companySettings.breakRoundingMinimumPreference,
    timeRosterPublicTokenPreference:
      companySettings.timeRosterPublicTokenPreference,
    xeroFreightAccountPreference: companySettings.xeroFreightAccountPreference,
    teamAppEnabled: companySettings.teamAppEnabled,
    teamAppDisplayPayInformation: companySettings.teamAppDisplayPayInformation,
    teamAppDisplayPhoneNumbers: companySettings.teamAppDisplayPhoneNumbers,
  }

  cachedCompanySettings = { companyId, settings }
  return settings
}

export const clearCompanySettingsCache = (): void => {
  cachedCompanySettings = null
}

export const exceptionMessageHandler = (
  error: unknown,
  errorMessage?: string
) => {
  logger.error('Exception caught %o, with message %o', error, errorMessage)
  // LoadedError is the old error type we used before having the loaded SDKs
  // and we need to keep it for backwards compatibility
  if (error instanceof LoadedError) {
    enqueueLoadedSnackbar(
      error.message,
      'error',
      error.details,
      error.id,
      error.code
    )
    return
  }

  // CoreApiError is the error type sent by the core API
  if (error instanceof CoreApiError) {
    enqueueLoadedSnackbar(
      error.description,
      'error',
      error.messages,
      error.id,
      error.code
    )
    return
  }

  if (error instanceof Error && error.name === 'AbortError') {
    // If we abort a request, we don't want to show an error message
    // We expect to have a custom message stating that the operation was cancelled
    return
  }

  enqueueLoadedSnackbar(errorMessage ?? 'The operation has failed', 'error')
}

/**
 * IRD formatter
 * e.g. 123456789 retrieves 123-456-789 - because ird number length is 9
 * e.g. 12345678 retrieves 12-345-678 - because ird number length is 8
 * @param irdNumber with hyphens
 * @returns
 */
export const formatIRD = (irdNumber: Nullable<string>) => {
  if (!irdNumber) return

  let irdNumberResult: string | null
  if (irdNumber?.length === 9) {
    irdNumberResult = valueFormatter(irdNumber, [3, 6], '-')
  } else if (irdNumber?.length === 8) {
    irdNumberResult = valueFormatter(irdNumber, [2, 5], '-')
  } else {
    irdNumberResult = null
  }
  return irdNumberResult
}

/**
 * BankAccountNumber formatter
 * e.g NZ. 3211231232131231 retrieves 32-1123-1232131-231
 * e.g NZ. 321123123213123 retrieves 32-1123-1232131-23
 * e.g AU. 1234567891234 retrieves 123-456-7891234
 * e.g AU. 123456789123 retrieves 123-456-789123
 * @param bankAccountNumber with hyphens
 * @returns
 */
export const formatBankAccountNumber = (
  bankAccountNumber: Nullable<string>,
  country: Country
) => {
  let bankAccountNumberResult: string | null
  if (country === Country.NewZealand) {
    bankAccountNumberResult = bankAccountNumber
      ? valueFormatter(bankAccountNumber, [2, 6, 13], '-')
      : null
  } else if (country === Country.Australia) {
    bankAccountNumberResult = bankAccountNumber
      ? valueFormatter(bankAccountNumber, [3, 6], '-')
      : null
  } else {
    bankAccountNumberResult = null
  }

  return bankAccountNumberResult
}

/**
 * BSB formatter
 * @param bsb with hyphens
 * e.g. 1234567891234 retrieves 123-456-7891234
 * e.g. 123456789123 retrieves 123-456-789123
 * @returns bsb with hyphens
 */
export const formatBSB = (bsb: Nullable<string>) => {
  if (!bsb) return null

  const bsbResult = valueFormatter(bsb, [3, 6], '-')
  return bsbResult
}

/**
 * TaxFileNumber formatter
 * @param taxFileNumber with hyphens
 * e.g. 321312313 retrieves 321-312-313
 * @returns taxFileNumber with hyphens
 */
export const formatTaxFileNumber = (taxFileNumber: Nullable<string>) => {
  if (!taxFileNumber) return null

  const taxFileNumberResult = valueFormatter(taxFileNumber, [3, 6], '-')
  return taxFileNumberResult
}

/**
 * Bank statement text builder and formatter
 * Currently hard coded as 'Wages [Company Name]'
 * Xero only allows letters and numbers and an 18 character limit, so we format to match that.
 * We remove all other characters from the company name then trim it to remove any unexpected spaces.
 * Then reduce the whole string down to 18 characters
 * @param the value to add to the bank statement text
 * @returns a bank statement text in a valid format for sending to Xero
 */
export const buildStatementText = (value: Nullable<string>) => {
  if (!value) return 'Wages'
  return `Wages ${value.replace(/[^\w\s]/g, '').trim()}`.substring(0, 18)
}

/**
 * Triggers the download of a file for a given filename and URL.createObjectURL object.
 *
 * Note that this does not manage the revocation of the object URL, so the caller should
 * do that to avoid memory leaks.
 * @param fileName
 * @param fileObjectURL
 */
export const downloadFile = (fileName, fileObjectURL) => {
  const link = document.createElement('a')
  link.href = fileObjectURL
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Returns true if a user has opted into the alpha UI features.
 */
export const isAlphaUIEnabled = () =>
  !!window.localStorage.getItem('@@loaded/alpha-styles')

/**
 * Returns true if a user has opted into the beta UI features.
 */
export const isBetaUIEnabled = () =>
  !!window.localStorage.getItem('@@loaded/beta-styles')

export const triggerCustomEvent = (eventName: string) => {
  // Create and dispatch the custom event
  const event = new Event(eventName)
  window.dispatchEvent(event)
}

/**
 * Rounds a value and returns the rounded number as a string
 * @param value the value to be rounded
 * @param dp the decimal places to round the value to
 * @returns rounded number as a string or undefined
 */
export const roundValue = (
  value: number | undefined,
  dp: 2 | 3 | 4
): string | undefined => {
  // Round to given decimal places and make sure there are always the given decimal places
  return value != undefined
    ? (Math.round(value * 10 ** dp) / 10 ** dp).toFixed(dp)
    : undefined
}

/**
 * Rounds a value and returns the rounded number as a string
 * @param value the value to be rounded
 * @param dp the decimal places to round the value to
 * @returns rounded number as a string or undefined
 */
export const roundNumber = (value: number | undefined, dp: 2 | 3): number => {
  // Round to given decimal places and make sure there are always the given decimal places
  return parseFloat(roundValue(value == undefined ? 0 : value, dp)!)
}

/**
 * Rounds a value up to the nearest whole number or one decimal place and returns as a number
 * @param value the value to be rounded up
 * @param dp the decimal places to round up to (0 or 1)
 * @returns rounded up number as a number
 */
export const roundUpToDecimalPlaces = (
  value: number | undefined,
  dp: 0 | 1
): number => {
  const safeValue = value == undefined ? 0 : value
  const factor = 10 ** dp
  return Math.ceil(safeValue * factor) / factor
}

/**
 * Finds a matching item in the provided data based on the matchProperty and searchTerm.
 * @template T - The type of the items in the data record.
 * @param {Record<string, T>} data - The record of data to search in.
 * @param {keyof T} matchProperty - The property to match against the searchTerm.
 * @param {string | undefined} searchTerm - The term to search for in the matchProperty.
 * @param {boolean} [ignoreCase=true] - Whether to ignore case while matching (default is true).
 * @returns {T | undefined} - The matching item, or undefined if no match is found.
 */
export const findMatching = <T>(
  data: Record<string, T>,
  matchProperty: keyof T,
  searchTerm: string | undefined,
  ignoreCase: boolean = true // New parameter, defaults to true
): T | undefined => {
  if (!searchTerm) return undefined

  return Object.values(data).find((item) => {
    const itemValue = item[matchProperty]?.toString()
    if (!itemValue) return false

    return ignoreCase
      ? itemValue.toLowerCase() === searchTerm.toLowerCase()
      : itemValue === searchTerm
  })
}

/**
 * Cancel any queries that are actively fetching data
 */
export const abortFetchingQueries = async (
  queryFilters: QueryFilters,
  cancelOptions?: CancelOptions
) => {
  await singletonQueryClient.cancelQueries(queryFilters, cancelOptions)
}

/**
 * Turns a Base64 encoded string into a Blob
 * @param base64 The Base64 encoded string representing a blob
 * @param contentType the content type of the blob
 * @returns A Blob of the Base64 encoded string
 */
export const base64ToBlob = (base64?: string, contentType?: string): Blob => {
  if (!base64 || !contentType) {
    return new Blob()
  }

  const byteCharacters = atob(base64)
  const byteArrays: Uint8Array<ArrayBuffer>[] = []

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512)
    const byteNumbers = new Array(slice.length)

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

/**
 * Takes an array of strings and converts it to a human readable list in the format
 * "item1, item2, and item3"
 * @param stringArray The array of strings to make human readable
 * @returns a human readable list
 */
export const stringArrayToHumanReadableList = (
  stringArray: string[]
): string => {
  let fieldsList: string
  if (stringArray.length === 1) {
    fieldsList = stringArray[0]
  } else if (stringArray.length === 2) {
    fieldsList = `${stringArray[0]} and ${stringArray[1]}`
  } else {
    const allButLast = stringArray.slice(0, -1).join(', ')
    const last = stringArray.at(-1)!
    fieldsList = `${allButLast}, and ${last}`
  }

  return fieldsList
}
